import { useServiceToken } from '@ltvco/refresh-lib/v1';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@ltvco/refresh-lib/vendors';
import { useCallback } from 'react';

import {
  type BulkReportCsvParams,
  type CreateListBulkReportParams,
  type ListBulkReportCurrentResponse,
} from 'api/lizanoBulkReports/interfaces';
import {
  downloadCsvAsFile,
  fetchListBulkReportCurrentValidated,
  lizanoBulkReportRequests,
} from 'api/lizanoBulkReports/lizanoBulkReports';

export const normalizeListBulkReportStatus = (raw: unknown): string =>
  String(raw ?? '')
    .trim()
    .toLowerCase();

const DEFAULT_LIST_BULK_REPORT_CURRENT_POLL_MS = 15000;

export const listBulkReportCurrentQueryKey = (bulkReportType: string) =>
  ['lizanoListBulkReportCurrent', bulkReportType] as const;

const getListBulkReportCurrentRefetchInterval = (
  data: ListBulkReportCurrentResponse | undefined,
  pollWhenActiveMs: number
): number | false => {
  if (!data) {
    return false;
  }
  const status = normalizeListBulkReportStatus(data.status);
  if (status === 'pending' || status === 'processing') {
    return pollWhenActiveMs;
  }
  return false;
};

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    const onAbort = (): void => {
      clearTimeout(timeoutId);
      signal?.removeEventListener('abort', onAbort);
      reject(new DOMException('Aborted', 'AbortError'));
    };

    const timeoutId = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);

    signal?.addEventListener('abort', onAbort);
  });

export interface DownloadBulkReportCsvMutationInput
  extends BulkReportCsvParams {
  downloadFilename?: string;
}

export interface WaitForListBulkReportTerminalOptions {
  intervalMs?: number;
  signal?: AbortSignal;
  onStatus?: (status: string) => void;
  expectedBulkReportId?: string;
  timeoutMs?: number;
}

const ACTIVE_LIST_BULK_REPORT_STATUSES = new Set(['pending', 'processing']);

const readCurrentBulkReportId = (
  data: ListBulkReportCurrentResponse
): string => {
  const raw = data.bulk_report_id;
  return raw != null ? String(raw) : '';
};

export async function waitForListBulkReportTerminalWithQueryClient(
  bulkReportType: string,
  token: string,
  options?: WaitForListBulkReportTerminalOptions
): Promise<ListBulkReportCurrentResponse> {
  const intervalMs =
    options?.intervalMs ?? DEFAULT_LIST_BULK_REPORT_CURRENT_POLL_MS;
  const startedAt = Date.now();

  for (;;) {
    if (options?.signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }
    if (
      options?.timeoutMs != null &&
      Date.now() - startedAt > options.timeoutMs
    ) {
      throw new Error(
        `Timed out after ${options.timeoutMs}ms waiting for bulk report to finish`
      );
    }

    const data = await fetchListBulkReportCurrentValidated(
      bulkReportType,
      token
    );

    const status = normalizeListBulkReportStatus(data.status);
    options?.onStatus?.(status);

    if (status === 'failed') {
      throw new Error('Bulk report job failed');
    }
    if (status === 'completed') {
      const expected = options?.expectedBulkReportId?.trim();
      if (expected) {
        const currentId = readCurrentBulkReportId(data);
        if (currentId !== expected) {
          await sleep(intervalMs, options?.signal);
          continue;
        }
      }
      return data;
    }

    if (!ACTIVE_LIST_BULK_REPORT_STATUSES.has(status)) {
      throw new Error(
        `Bulk report job returned non-active status: ${status || '(empty)'}`
      );
    }

    await sleep(intervalMs, options?.signal);
  }
}

export interface UseLizanoListBulkReportCurrentQueryArgs {
  bulkReportType: string;
  enabled?: boolean;
  refetchIntervalMs?: number;
}

export const useLizanoListBulkReportCurrentQuery = ({
  bulkReportType,
  enabled = true,
  refetchIntervalMs = DEFAULT_LIST_BULK_REPORT_CURRENT_POLL_MS,
}: UseLizanoListBulkReportCurrentQueryArgs) => {
  const { data: lizanoTokenData } = useServiceToken('lizano');
  const token = lizanoTokenData?.token;

  return useQuery({
    queryKey: listBulkReportCurrentQueryKey(bulkReportType),
    queryFn: () => {
      if (!token) {
        throw new Error('Missing Lizano service token');
      }
      return fetchListBulkReportCurrentValidated(bulkReportType, token);
    },
    enabled: Boolean(enabled && token),
    refetchIntervalInBackground: false,
    refetchInterval: (data, _query) =>
      getListBulkReportCurrentRefetchInterval(data, refetchIntervalMs),
  });
};

export const useLizanoBulkReports = () => {
  const queryClient = useQueryClient();
  const { data: lizanoTokenData } = useServiceToken('lizano');
  const token = lizanoTokenData?.token;

  const createListBulkReport = useMutation({
    mutationFn: (params: CreateListBulkReportParams) => {
      if (!token) {
        throw new Error('Missing Lizano service token');
      }
      return lizanoBulkReportRequests.createListBulkReport(params, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lists'] });
      queryClient.invalidateQueries({
        queryKey: ['userListBulkReports', 'contact-cleaner'],
      });
    },
  });

  const downloadBulkReportCsv = useMutation({
    mutationFn: async ({
      listCode,
      bulkReportId,
      downloadFilename,
    }: DownloadBulkReportCsvMutationInput) => {
      if (!token) {
        throw new Error('Missing Lizano service token');
      }
      const csv = await lizanoBulkReportRequests.getBulkReportCsv(
        listCode,
        bulkReportId,
        token
      );
      if (downloadFilename) {
        downloadCsvAsFile(csv, downloadFilename);
      }
      return csv;
    },
  });

  const fetchListBulkReportCurrent = useCallback(
    async (bulkReportType: string) => {
      if (!token) {
        throw new Error('Missing Lizano service token');
      }
      return fetchListBulkReportCurrentValidated(bulkReportType, token);
    },
    [token]
  );

  const fetchListBulkReports = useCallback(
    async (bulkReportType: string) => {
      if (!token) {
        throw new Error('Missing Lizano service token');
      }
      return lizanoBulkReportRequests.getListBulkReports(bulkReportType, token);
    },
    [token]
  );

  const fetchBulkReportCsv = useCallback(
    async (listCode: string, bulkReportId: string) => {
      if (!token) {
        throw new Error('Missing Lizano service token');
      }
      return lizanoBulkReportRequests.getBulkReportCsv(
        listCode,
        bulkReportId,
        token
      );
    },
    [token]
  );

  const waitForListBulkReportTerminal = useCallback(
    async (
      bulkReportType: string,
      options?: WaitForListBulkReportTerminalOptions
    ) => {
      if (!token) {
        throw new Error('Missing Lizano service token');
      }
      return waitForListBulkReportTerminalWithQueryClient(
        bulkReportType,
        token,
        options
      );
    },
    [queryClient, token]
  );

  return {
    lizanoToken: token,
    createListBulkReport,
    downloadBulkReportCsv,
    fetchListBulkReportCurrent,
    fetchListBulkReports,
    fetchBulkReportCsv,
    waitForListBulkReportTerminal,
    downloadCsvAsFile,
  };
};
